import { useState } from 'react'
import { ExternalLink, KeyRound } from 'lucide-react'
import { useAnswerAccessRequestMutation, useGetProjectAccessQuery, useSaveVercelAccessMutation } from '../../services/projectDataApi'
import { accessGuidance } from '../../lib/accessBlockers'
import { distinctAccessRequests } from '../../lib/accessRequests'
import { PhoneNumberSchema, type AccessRequest } from '../../schemas/projectAccess'
import './chat.css'

function AccessRequestCard({ projectId, request }: { projectId: string; request: AccessRequest }) {
  const guidance = accessGuidance(request.provider, request)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [answer, mutation] = useAnswerAccessRequestMutation()
  if (answered) return <p role="status">Access response sent. QA is rechecking this run.</p>
  return (
    <form className="chat-form" data-testid="chat-access-request" onSubmit={async event => {
      event.preventDefault(); setError(null)
      if (guidance.input === 'phone' && !PhoneNumberSchema.safeParse(value).success) {
        setError('Enter a complete phone number in the format requested by the blocked page.'); return
      }
      try {
        await answer({ projectId, requestId: request.id, value: guidance.input === 'retry' ? '' : value.trim() }).unwrap()
        setValue(''); setAnswered(true)
      } catch { setError('Could not answer this request. Only the project owner or an admin can respond, and the run must still be waiting.') }
      finally { mutation.reset() }
    }}>
      <div className="chat-form__head"><KeyRound size={15} aria-hidden /><span className="chat-form__title">{guidance.title}</span></div>
      <p className="chat-form__hint">{request.detail}</p>
      <p className="chat-form__hint">Blocked page: <a href={request.url} target="_blank" rel="noreferrer">{new URL(request.url).host}</a></p>
      <p className="chat-form__hint">{guidance.instructions}</p>
      {guidance.docs && <p className="chat-form__hint">
        <a className="chat-form__guide-link" href={guidance.docs} target="_blank" rel="noreferrer">
          {request.provider === 'cloudflare' || request.provider === 'turnstile' ? 'How to disable Cloudflare bot protection' : request.provider === 'vercel' ? 'Set up Vercel preview access' : 'Open setup guide'} <ExternalLink size={14} aria-hidden />
        </a>
      </p>}
      <p className="chat-form__hint">Waiting until {new Date(request.expires_at).toLocaleTimeString()}.{guidance.input === 'otp' && ' Codes expire quickly; submit the current code.'}</p>
      {guidance.input !== 'retry' && <label className="chat-form__hint">
        {guidance.input === 'otp' ? 'Verification code' : guidance.input === 'phone' ? 'Phone number' : 'Vercel automation bypass secret'}
        <input className="chat-form__input fs-mask rr-mask" data-private="true" type="password" autoComplete="off"
          inputMode={guidance.input === 'phone' ? 'tel' : guidance.input === 'otp' && request.verification_channel !== 'email' ? 'numeric' : 'text'} maxLength={guidance.input === 'otp' ? 12 : guidance.input === 'phone' ? 32 : 1024}
          value={value} onChange={event => setValue(event.target.value)} required disabled={mutation.isLoading} />
      </label>}
      {error && <p className="pchat__error" role="alert">{error}</p>}
      <div className="chat-form__actions"><button type="submit" className="chat-form__submit" disabled={mutation.isLoading}>
        {mutation.isLoading ? 'Sending…' : guidance.input === 'retry' ? 'Protection updated — retry' : guidance.input === 'otp' ? 'Use code in this run' : guidance.input === 'phone' ? 'Use phone number in this run' : 'Save secret and retry'}
      </button></div>
    </form>
  )
}

export default function ChatAccessRequests({ projectId }: { projectId: string }) {
  const query = useGetProjectAccessQuery(projectId, { pollingInterval: 5000, skipPollingIfUnfocused: true })
  return <>{distinctAccessRequests(query.currentData?.requests ?? []).map(request => <AccessRequestCard key={request.id} projectId={projectId} request={request} />)}</>
}

function VercelAccessEditor({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const query = useGetProjectAccessQuery(projectId)
  const [editing, setEditing] = useState(false)
  const [secret, setSecret] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [save, mutation] = useSaveVercelAccessMutation()
  const guidance = accessGuidance('vercel')
  const write = async (value: string | null) => {
    setError(null)
    try { await save({ projectId, secret: value }).unwrap(); setSecret(''); setEditing(false) }
    catch { setError('Could not save deployment access. Try again.') }
    finally { mutation.reset() }
  }
  return <div className="chat-form" data-testid="vercel-deployment-access">
    <div className="chat-form__head"><KeyRound size={15} aria-hidden /><span className="chat-form__title">Vercel preview access</span></div>
    <p className="chat-form__hint">{query.isFetching ? 'Loading…' : query.isError ? 'Access settings unavailable.' : query.currentData?.vercel_configured ? 'Bypass secret configured.' : 'No bypass secret configured.'}</p>
    <p className="chat-form__hint">{guidance.instructions}</p>
    <p className="chat-form__hint"><a className="chat-form__guide-link" href={guidance.docs!} target="_blank" rel="noreferrer">Set up Vercel preview access <ExternalLink size={14} aria-hidden /></a></p>
    <p className="chat-form__hint">Vercel secrets grant access across that Vercel project. Use a dedicated secret you can revoke. App sign-in credentials are configured separately.</p>
    {editing && <form onSubmit={event => { event.preventDefault(); void write(secret.trim()) }}>
      <label className="chat-form__hint">Automation bypass secret
        <input className="chat-form__input fs-mask rr-mask" data-private="true" type="password" autoComplete="off" value={secret} onChange={event => setSecret(event.target.value)} required maxLength={1024} disabled={mutation.isLoading} />
      </label>
      <div className="chat-form__actions">
        <button className="chat-form__submit" type="submit" disabled={mutation.isLoading}>{mutation.isLoading ? 'Saving…' : 'Save secret'}</button>
        <button className="chat-form__cancel" type="button" disabled={mutation.isLoading} onClick={() => { setEditing(false); setSecret(''); setError(null) }}>Cancel</button>
      </div>
    </form>}
    {canEdit && !editing && !query.isError && <div className="chat-form__actions">
      <button type="button" className="chat-form__submit" disabled={query.isLoading || mutation.isLoading} onClick={() => setEditing(true)}>{query.currentData?.vercel_configured ? 'Replace secret' : 'Set up bypass'}</button>
      {query.currentData?.vercel_configured && <button type="button" className="chat-form__cancel" disabled={mutation.isLoading} onClick={() => { void write(null) }}>Remove secret</button>}
    </div>}
    {error && <p className="pchat__error" role="alert">{error}</p>}
  </div>
}

export function VercelAccessSettings(props: { projectId: string; canEdit: boolean }) {
  return <VercelAccessEditor key={props.projectId} {...props} />
}
